import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  Button,
  Fade,
  Collapse,
  Badge,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Chat,
  Close,
  Send,
  SmartToy,
  Person,
  ExpandMore,
  ExpandLess,
  Refresh,
  Settings,
  CheckCircle,
  Error
} from '@mui/icons-material';

const PersistentChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: 'Hi! I\'m your study assistant. I keep track of all your questions and answers to help you learn better!',
      timestamp: Date.now()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [tempApiKey, setTempApiKey] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // 'disconnected', 'connecting', 'connected', 'error'
  const [connectionMessage, setConnectionMessage] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!isOpen && messages.length > 1) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.type === 'bot' && Date.now() - lastMessage.timestamp < 5000) {
        setUnreadCount(prev => prev + 1);
      }
    }
  }, [messages, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  useEffect(() => {
    // Check for stored API key on component mount
    const storedKey = localStorage.getItem('openai_api_key');
    if (storedKey) {
      setApiKey(storedKey);
      checkConnection(storedKey);
    } else {
      setConnectionMessage('API key not configured');
    }
  }, []);

  const checkConnection = async (key = apiKey) => {
    if (!key) {
      setConnectionStatus('disconnected');
      setConnectionMessage('No API key provided');
      return;
    }

    setConnectionStatus('connecting');
    setConnectionMessage('Testing connection...');

    try {
      // Test the OpenAI API key with a simple request
      const response = await fetch('https://api.openai.com/v1/models', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${key}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setConnectionStatus('connected');
        setConnectionMessage('Connected to OpenAI API');
      } else if (response.status === 401) {
        setConnectionStatus('error');
        setConnectionMessage('Invalid API key');
      } else {
        setConnectionStatus('error');
        setConnectionMessage(`Connection failed: ${response.statusText}`);
      }
    } catch (error) {
      setConnectionStatus('error');
      setConnectionMessage(`Connection error: ${error.message}`);
    }
  };

  const handleSaveApiKey = () => {
    if (tempApiKey.trim()) {
      setApiKey(tempApiKey);
      localStorage.setItem('openai_api_key', tempApiKey);
      checkConnection(tempApiKey);
      setShowApiKeyDialog(false);
      setTempApiKey('');
    }
  };

  const handleRemoveApiKey = () => {
    setApiKey('');
    localStorage.removeItem('openai_api_key');
    setConnectionStatus('disconnected');
    setConnectionMessage('API key removed');
    setShowApiKeyDialog(false);
  };

  const handleSendMessage = async () => {
    if (!inputText.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: inputText,
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsTyping(true);

    // Generate AI response
    try {
      const responseText = await generateBotResponse(inputText);
      const botResponse = {
        id: Date.now() + 1,
        type: 'bot',
        text: responseText,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, botResponse]);
    } catch (error) {
      const errorResponse = {
        id: Date.now() + 1,
        type: 'bot',
        text: "Sorry, I encountered an error while processing your message. Please try again.",
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorResponse]);
    }
    setIsTyping(false);
  };

  const generateBotResponse = async (userInput) => {
    if (connectionStatus !== 'connected' || !apiKey) {
      return "I need an OpenAI API key to provide intelligent responses. Please click the settings button to configure your API key.";
    }

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            {
              role: 'system',
              content: 'You are a helpful study assistant. Keep responses concise and educational, focusing on helping students learn effectively.'
            },
            {
              role: 'user',
              content: userInput
            }
          ],
          max_tokens: 150,
          temperature: 0.7
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.choices[0].message.content.trim();
      } else {
        throw new Error(`API request failed: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error generating response:', error);
      return "I'm having trouble connecting to the AI service. Please check your internet connection and try again.";
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1300
      }}
    >
      <Collapse in={isOpen} orientation="vertical">
        <Paper
          elevation={8}
          sx={{
            width: 320,
            height: 450,
            mb: 1,
            borderRadius: 3,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Chat Header */}
          <Box
            sx={{
              p: 2,
              backgroundColor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToy />
              <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                Study Assistant
              </Typography>
              <Chip
                label={
                  connectionStatus === 'connected' ? 'Online' :
                  connectionStatus === 'connecting' ? 'Connecting' :
                  connectionStatus === 'error' ? 'Error' : 'Offline'
                }
                size="small"
                icon={
                  connectionStatus === 'connected' ? <CheckCircle sx={{ fontSize: '12px !important' }} /> :
                  connectionStatus === 'connecting' ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> :
                  connectionStatus === 'error' ? <Error sx={{ fontSize: '12px !important' }} /> : null
                }
                sx={{
                  backgroundColor: 
                    connectionStatus === 'connected' ? 'success.light' :
                    connectionStatus === 'error' ? 'error.light' : 'warning.light',
                  color: 
                    connectionStatus === 'connected' ? 'success.dark' :
                    connectionStatus === 'error' ? 'error.dark' : 'warning.dark',
                  fontSize: '0.7rem'
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => checkConnection()}
                sx={{ color: 'white' }}
                title="Refresh connection"
              >
                <Refresh fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setShowApiKeyDialog(true)}
                sx={{ color: 'white' }}
                title="Configure API key"
              >
                <Settings fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setIsOpen(false)}
                sx={{ color: 'white' }}
              >
                {isOpen ? <ExpandMore /> : <ExpandLess />}
              </IconButton>
            </Box>
          </Box>

          {/* Messages Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 1,
              backgroundColor: '#f5f5f5'
            }}
          >
            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                  mb: 1
                }}
              >
                <Box
                  sx={{
                    maxWidth: '80%',
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: message.type === 'user' ? 'primary.main' : 'white',
                    color: message.type === 'user' ? 'white' : 'text.primary',
                    boxShadow: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    {message.type === 'user' ? <Person sx={{ fontSize: 14 }} /> : <SmartToy sx={{ fontSize: 14 }} />}
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {formatTimestamp(message.timestamp)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                    {message.text}
                  </Typography>
                </Box>
              </Box>
            ))}
            
            {isTyping && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: 'white',
                    boxShadow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <SmartToy sx={{ fontSize: 14 }} />
                  <Typography variant="body2" sx={{ fontStyle: 'italic', opacity: 0.7 }}>
                    Typing...
                  </Typography>
                </Box>
              </Box>
            )}
            
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              backgroundColor: 'background.paper'
            }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Ask me anything about your studies..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                multiline
                maxRows={3}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleSendMessage}
                disabled={!inputText.trim() || isTyping}
                sx={{ minWidth: 'auto', px: 1.5 }}
              >
                <Send fontSize="small" />
              </Button>
            </Box>
          </Box>
        </Paper>
      </Collapse>

      {/* Floating Chat Button */}
      <Fade in={!isOpen}>
        <Paper
          elevation={6}
          sx={{
            display: isOpen ? 'none' : 'flex',
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: 'primary.main',
            cursor: 'pointer',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            position: 'relative',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: 12
            }
          }}
          onClick={() => setIsOpen(true)}
        >
          <Badge badgeContent={unreadCount} color="error">
            <Chat sx={{ color: 'white', fontSize: 24 }} />
          </Badge>
          {/* Connection status indicator */}
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: 
                connectionStatus === 'connected' ? 'success.main' :
                connectionStatus === 'error' ? 'error.main' :
                connectionStatus === 'connecting' ? 'warning.main' : 'grey.500',
              border: '2px solid white',
              boxShadow: 1
            }}
          />
        </Paper>
      </Fade>

      {/* API Key Configuration Dialog */}
      <Dialog
        open={showApiKeyDialog}
        onClose={() => setShowApiKeyDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Configure OpenAI API Key</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              To enable AI-powered responses, please provide your OpenAI API key.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Your API key will be stored locally in your browser and used only to communicate with OpenAI.
            </Alert>
          </Box>
          
          {connectionMessage && (
            <Alert 
              severity={
                connectionStatus === 'connected' ? 'success' :
                connectionStatus === 'error' ? 'error' : 'info'
              } 
              sx={{ mb: 2 }}
            >
              {connectionMessage}
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="OpenAI API Key"
            type="password"
            value={tempApiKey}
            onChange={(e) => setTempApiKey(e.target.value)}
            placeholder="sk-..."
            sx={{ mb: 2 }}
          />
          
          {apiKey && (
            <Typography variant="body2" color="text.secondary">
              Current key: {apiKey.substring(0, 10)}...{apiKey.substring(apiKey.length - 4)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {apiKey && (
            <Button onClick={handleRemoveApiKey} color="error">
              Remove Key
            </Button>
          )}
          <Button onClick={() => setShowApiKeyDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveApiKey} 
            variant="contained"
            disabled={!tempApiKey.trim()}
          >
            Save & Test
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PersistentChatBot;